import { supabase } from '../lib/supabase';
import { USE_IMMEDIATE_PERSIST, DEBUG_SESSION_LOGS } from '../utils/featureFlags';
import { generateUUID } from '../utils/uuid';
import { retrySupabaseOperation } from '../utils/retryUtils';
import { 
  sessionLogger, 
  logSessionStart, 
  logSessionEnd, 
  logSessionPersist, 
  logHeartbeat, 
  logSessionError 
} from '../utils/logger';

/**
 * LearningSessionService - 学習セッション管理サービス
 * フェーズA: 即時DB永続化対応版
 */
class LearningSessionService {
  constructor() {
    this.sessions = new Map(); // videoId -> session data (メモリキャッシュとして維持)
    this.heartbeatIntervals = new Map(); // videoId -> interval ID
    this.pausedSessions = new Map(); // 一時停止中のセッション管理
    this.HEARTBEAT_INTERVAL = 20000; // 20秒間隔
    
    // フェーズA: ページ離脱時のハンドラー設定
    if (USE_IMMEDIATE_PERSIST && typeof window !== 'undefined') {
      this._setupUnloadHandlers();
    }
  }

  /**
   * 動画視聴セッションを開始
   * フェーズA: 即時DB永続化対応
   */
  async startVideoSession(videoId, courseId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('User not authenticated, cannot start learning session');
        return null;
      }

      // フェーズA: 既存のアクティブセッションを復元を試行
      if (USE_IMMEDIATE_PERSIST) {
        const existingSession = await this._restoreActiveSession(videoId, user.id);
        if (existingSession) {
          // 既存セッションを復元
          this.sessions.set(videoId, existingSession);
          this._startHeartbeat(videoId);
          
          logSessionStart(videoId, existingSession.session_uuid, { 
            restored: true,
            totalSeconds: existingSession.total_seconds
          });
          
          return existingSession;
        }
      }

      // 既存のメモリセッションがあれば終了
      if (this.sessions.has(videoId)) {
        await this.endVideoSession(videoId);
      }

      const sessionUuid = generateUUID();
      const startedAt = new Date().toISOString();

      const session = {
        user_id: user.id,
        session_uuid: sessionUuid,
        video_id: videoId,
        course_id: courseId,
        started_at: startedAt,
        last_update: startedAt,
        total_seconds: 0,
        status: 'in_progress'
      };

      // メモリにセッション保存
      this.sessions.set(videoId, session);

      // ログ記録
      logSessionStart(videoId, sessionUuid, { 
        useImmediatePersist: USE_IMMEDIATE_PERSIST,
        courseId 
      });

      // フェーズA: 即時DB永続化
      if (USE_IMMEDIATE_PERSIST) {
        await this._persistSessionToDB(session, 'start');
        this._startHeartbeat(videoId);
      }

      return session;
    } catch (error) {
      console.error('Error starting video session:', error);
      return null;
    }
  }

  /**
   * 動画視聴セッションを更新（定期的に呼ばれる）
   * フェーズA: 即時DB永続化対応
   */
  async updateVideoSession(videoId, _currentTimeSeconds) {
    try {
      const session = this.sessions.get(videoId);
      if (!session) {
        console.warn('No active session for video:', videoId);
        return null;
      }

      const now = new Date();
      const lastUpdate = new Date(session.last_update);
      const timeDiffSeconds = Math.floor((now - lastUpdate) / 1000);

      // 一時停止中の場合は更新をスキップ
      if (this.pausedSessions.has(videoId)) {
        const pauseInfo = this.pausedSessions.get(videoId);
        console.log(`⏸️ Session paused for video ${videoId}, skipping update (paused at: ${pauseInfo.totalSeconds}s)`);
        return session;
      }

      // 妥当な時間差の場合のみ更新（最大10分）
      if (timeDiffSeconds > 0 && timeDiffSeconds <= 600) {
        session.total_seconds += timeDiffSeconds;
        session.last_update = now.toISOString();
        
        // ハートビートログ
        logHeartbeat(videoId, session.total_seconds, { 
          deltaSeconds: timeDiffSeconds,
          sessionUuid: session.session_uuid,
          actualSeconds: session.total_seconds
        });

        // フェーズA: 即時DB永続化
        if (USE_IMMEDIATE_PERSIST) {
          try {
            await this._persistSessionToDB(session, 'update');
          } catch (error) {
            // エラーをログに記録するが、処理は継続
            logSessionError(videoId, error, 'update', { sessionUuid: session.session_uuid });
          }
        }
      }

      return session;
    } catch (error) {
      console.error('Error updating video session:', error);
      return null;
    }
  }

  /**
   * 動画視聴セッションを終了してデータベースに保存
   * フェーズA: 即時DB永続化対応
   */
  async endVideoSession(videoId) {
    try {
      const session = this.sessions.get(videoId);
      if (!session) {
        console.warn('No active session to end for video:', videoId);
        return null;
      }

      // ハートビート停止
      this._stopHeartbeat(videoId);

      // 最後の更新を反映
      const now = new Date();
      const lastUpdate = new Date(session.last_update);
      const timeDiffSeconds = Math.floor((now - lastUpdate) / 1000);
      
      if (timeDiffSeconds > 0 && timeDiffSeconds <= 600) {
        session.total_seconds += timeDiffSeconds;
      }

      // 一時停止中の場合は、一時停止状態をクリア
      if (this.pausedSessions.has(videoId)) {
        this.pausedSessions.delete(videoId);
      }

      // セッション状態を完了に変更
      session.status = 'completed';
      session.ended_at = now.toISOString();

      // 5秒未満のセッションは記録しない（意味のある視聴とみなさない）
      if (session.total_seconds < 5) {
        if (DEBUG_SESSION_LOGS) {
          console.log('Session too short, not recording:', {
            video_id: videoId,
            total_seconds: session.total_seconds,
            started_at: session.started_at,
            last_update: session.last_update
          });
        }
        this.sessions.delete(videoId);
        return null;
      }

      // 秒単位の正確な記録を保持し、実際の視聴時間のみで分数を計算
      const actual_seconds = session.total_seconds;
      const duration_min = Math.round(actual_seconds / 60); // 最小値強制を削除
      
      // 0分のセッションをチェック（デバッグ用）
      if (duration_min === 0 && actual_seconds > 0) {
        console.log(`⚠️ Short session recorded: ${actual_seconds}s (${duration_min}min) for video ${videoId}`);
      }

      if (USE_IMMEDIATE_PERSIST) {
        // フェーズA: 即時DB永続化で最終保存
        try {
          await this._persistSessionToDB(session, 'end');
        } catch (error) {
          console.error('❌ Failed to save final session to DB:', error);
          // フォールバック保存
          this._saveToLocalStorage(session);
        }
      } else {
        // 従来のロジック（レガシー保存）
        try {
          const { data, error } = await supabase
            .from('learning_sessions')
            .insert({
              user_id: session.user_id,
              course_id: session.course_id,
              session_type: 'learn',
              started_at: session.started_at,
              ended_at: session.ended_at,
              duration_min: duration_min
            });

          if (error) {
            if (error.code === '42P01') {
              console.warn('learning_sessions table does not exist in local environment');
            } else {
              console.error('Error saving learning session:', error);
            }
          } else {
            console.log('✅ Learning session saved successfully (legacy):', {
              video_id: videoId,
              duration_min: duration_min,
              total_seconds: session.total_seconds,
              inserted_data: data
            });
          }
        } catch (error) {
          console.error('Error in legacy session save:', error);
        }
      }

      // セッションをクリア
      this.sessions.delete(videoId);
      sessionLogger.clearSessionMetrics(videoId);

      // 終了ログ（秒単位の詳細情報も含む）
      logSessionEnd(videoId, session.session_uuid, session.total_seconds, {
        duration_min,
        actual_seconds,
        status: session.status
      });
      
      return { 
        ...session, 
        ended_at: session.ended_at, 
        duration_min,
        total_seconds: session.total_seconds
      };
    } catch (error) {
      console.error('Error ending video session:', error);
      return null;
    }
  }

  /**
   * アクティブセッション復元（フェーズA新機能）
   */
  async _restoreActiveSession(videoId, userId) {
    try {
      const { data, error } = await supabase.rpc('get_user_active_sessions', {
        p_user_id: userId
      });

      if (error) {
        console.warn('Failed to restore active sessions:', error);
        return null;
      }

      // 該当する動画のアクティブセッションを探す
      const activeSession = data?.find(session => session.video_id === videoId);
      if (!activeSession) return null;

      // セッションオブジェクトを復元
      const now = new Date();
      const startedAt = new Date(activeSession.started_at);
      const totalElapsedSeconds = Math.floor((now - startedAt) / 1000);

      const restoredSession = {
        user_id: userId,
        session_uuid: activeSession.session_uuid,
        video_id: videoId,
        course_id: activeSession.course_id,
        started_at: activeSession.started_at,
        last_update: now.toISOString(),
        total_seconds: activeSession.duration_min * 60, // 保存されている実際の秒数を使用
        status: 'in_progress'
      };

      if (DEBUG_SESSION_LOGS) {
        console.log('🔄 Session restored from DB:', {
          videoId,
          sessionUuid: activeSession.session_uuid,
          totalSeconds: restoredSession.total_seconds,
          originalDuration: activeSession.duration_min
        });
      }

      return restoredSession;
    } catch (error) {
      console.error('Error restoring active session:', error);
      return null;
    }
  }

  /**
   * DB永続化処理（フェーズA新機能）
   */
  async _persistSessionToDB(session, operation = 'update') {
    // 実際の視聴時間のみで分数を計算（最小値強制を削除）
    const duration_min = Math.round(session.total_seconds / 60);
    
    try {
      const result = await retrySupabaseOperation(async () => {
        const { data, error } = await supabase.rpc('upsert_learning_session', {
          p_user_id: session.user_id,
          p_session_uuid: session.session_uuid,
          p_video_id: session.video_id,
          p_course_id: null, // 動画セッションではcourse_idはNULL
          p_session_type: 'learn',
          p_started_at: session.started_at,
          p_ended_at: session.status === 'completed' ? new Date().toISOString() : null,
          p_duration_min: duration_min,
          p_status: session.status
        });

        if (error) throw error;
        return data;
      }, `persist_session_${operation}`);

      // 成功ログ
      logSessionPersist(session.video_id, session.session_uuid, true, operation, {
        duration_min,
        status: session.status
      });

      return result;
    } catch (error) {
      // エラーログ
      logSessionPersist(session.video_id, session.session_uuid, false, operation, {
        error: error.message,
        duration_min
      });
      
      // フォールバック: ローカルストレージに一時保存
      this._saveToLocalStorage(session);
      throw error;
    }
  }

  /**
   * ローカルストレージフォールバック
   */
  _saveToLocalStorage(session) {
    try {
      const key = `learning_session_${session.video_id}`;
      localStorage.setItem(key, JSON.stringify({
        ...session,
        saved_at: new Date().toISOString()
      }));
      if (DEBUG_SESSION_LOGS) {
        console.log('💿 Session saved to localStorage as fallback:', key);
      }
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  /**
   * ハートビート開始（20秒間隔でDB更新）
   */
  _startHeartbeat(videoId) {
    // 既存のハートビートがあれば停止
    this._stopHeartbeat(videoId);

    const intervalId = setInterval(async () => {
      try {
        await this.updateVideoSession(videoId, 0); // currentTimeSecondsは使わず、経過時間で計算
      } catch (error) {
        console.error(`❌ Heartbeat failed for video ${videoId}:`, error);
      }
    }, this.HEARTBEAT_INTERVAL);

    this.heartbeatIntervals.set(videoId, intervalId);

    if (DEBUG_SESSION_LOGS) {
      console.log(`💓 Heartbeat started for video ${videoId} (${this.HEARTBEAT_INTERVAL}ms interval)`);
    }
  }

  /**
   * ハートビート停止
   */
  _stopHeartbeat(videoId) {
    const intervalId = this.heartbeatIntervals.get(videoId);
    if (intervalId) {
      clearInterval(intervalId);
      this.heartbeatIntervals.delete(videoId);
      if (DEBUG_SESSION_LOGS) {
        console.log(`💓 Heartbeat stopped for video ${videoId}`);
      }
    }
  }

  /**
   * ページ離脱時の緊急保存
   */
  _setupUnloadHandlers() {
    const handleUnload = async () => {
      // すべてのアクティブセッションを即座に終了
      const videoIds = Array.from(this.sessions.keys());
      await Promise.all(videoIds.map(videoId => this.endVideoSession(videoId)));
    };

    // 複数のイベントに対応
    ['beforeunload', 'pagehide', 'visibilitychange'].forEach(event => {
      window.addEventListener(event, handleUnload);
    });

    // visibilitychange の特別処理
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && USE_IMMEDIATE_PERSIST) {
        // バックグラウンド時は即座にDB保存
        this.sessions.forEach(async (session, videoId) => {
          try {
            await this._persistSessionToDB(session, 'background');
          } catch (error) {
            console.error(`Failed to save session on background: ${videoId}`, error);
          }
        });
      }
    });
  }

  /**
   * すべてのアクティブなセッションを終了
   */
  async endAllSessions() {
    const videoIds = Array.from(this.sessions.keys());
    
    // すべてのハートビート停止
    videoIds.forEach(videoId => this._stopHeartbeat(videoId));
    
    const results = await Promise.all(
      videoIds.map(videoId => this.endVideoSession(videoId))
    );
    return results.filter(Boolean);
  }

  /**
   * セッションを一時停止
   */
  pauseVideoSession(videoId) {
    const session = this.sessions.get(videoId);
    if (!session) {
      console.warn('No active session to pause for video:', videoId);
      return false;
    }

    // 最後の更新から現在までの時間を加算してから一時停止
    const now = new Date();
    const lastUpdate = new Date(session.last_update);
    const timeDiffSeconds = Math.floor((now - lastUpdate) / 1000);
    
    if (timeDiffSeconds > 0 && timeDiffSeconds <= 600) {
      session.total_seconds += timeDiffSeconds;
      session.last_update = now.toISOString();
    }

    // 一時停止状態を記録
    this.pausedSessions.set(videoId, {
      pausedAt: now.toISOString(),
      totalSeconds: session.total_seconds
    });

    console.log(`⏸️ Session paused for video ${videoId} at ${session.total_seconds}s`);
    return true;
  }

  /**
   * セッションを再開
   */
  resumeVideoSession(videoId) {
    const session = this.sessions.get(videoId);
    if (!session) {
      console.warn('No active session to resume for video:', videoId);
      return false;
    }

    if (this.pausedSessions.has(videoId)) {
      const pauseInfo = this.pausedSessions.get(videoId);
      
      // 再開時に last_update を現在時刻に更新
      session.last_update = new Date().toISOString();
      
      this.pausedSessions.delete(videoId);
      console.log(`▶️ Session resumed for video ${videoId} from ${session.total_seconds}s`);
      return true;
    }

    return false;
  }

  /**
   * アクティブなセッションの存在確認
   */
  hasActiveSession(videoId) {
    return this.sessions.has(videoId);
  }

  /**
   * アクティブなセッション情報を取得
   */
  getActiveSession(videoId) {
    return this.sessions.get(videoId);
  }

  /**
   * セッションメトリクスの取得（A8: モニタリング用）
   */
  getSessionMetrics() {
    return sessionLogger.getMetrics();
  }

  /**
   * 詳細セッションレポートの取得（A8: モニタリング用）
   */
  getSessionReport() {
    return sessionLogger.getSessionReport();
  }

  /**
   * メトリクスリセット（テスト用）
   */
  resetMetrics() {
    sessionLogger.resetMetrics();
  }
}

// シングルトンインスタンスをエクスポート
export const learningSessionService = new LearningSessionService();
export default learningSessionService;